import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Api } from '../Api';
import { AppDispatch, RootState } from '../store';
import {
  IConfiguration,
  ISelectedOptions,
  IHint,
  ICalculation,
  IComponentGroup,
  IComponent,
  ErrorMessage,
  IOption
} from '../Interface/Interface';
import { METHOD_ADD_TO_CART } from '../Utils/createMessageInterface';
import { startAppListening } from '../listenerMiddleware';
import { createSlotInfoForOption } from '../Helpers/SlotInfoHelpers';
import { setAmountStepsForCPUComponent } from '../Helpers/CPUAmountStepHelpers';
import { setOptionFiltersForComponent } from '../Helpers/OptionFilterHelpers';
import { getSelectedDriveOptionsForRaidSlotConsumerOption } from '../Helpers/RaidLevelOptionHelpers';
import {
  WARRANTY_ADDONS_COMPONENT,
  WARRANTY_COMPONENT
} from '../Constants/Constants';
import {
  filterWarrantyAddonOptions,
  getWarrantyAddonOptionsForWarrantyOption
} from '../Helpers/WarrantyHelpers';

export interface ConfigurationState {
  configuration?: IConfiguration;
  groupedComponents?: IComponentGroup[];
  errorMessage?: ErrorMessage | ErrorMessage[];
  hints?: IHint[];
  isLoading: boolean;
  categoryTranslations: any;
  shareUrl?: string;
  shareCode?: string;
  numSelectedRaidControllers: number;
  calculation?: ICalculation;
  componentsWithOptions: IComponent[];
  warrantyAddonComponentOptions: IOption[];
  invalidState: boolean;
}

const initialState: ConfigurationState = {
  configuration: undefined,
  groupedComponents: undefined,
  errorMessage: undefined,
  hints: undefined,
  isLoading: false,
  categoryTranslations: {},
  shareUrl: undefined,
  numSelectedRaidControllers: 0,
  calculation: undefined,
  componentsWithOptions: [],
  warrantyAddonComponentOptions: [],
  invalidState: false
};

const updateGroupedComponents = (state: ConfigurationState) => {
  if (!state.configuration) {
    return;
  }
  const maxAmountChecks =
    state.configuration.check_results?.check_results
      ?.filter(
        ({ rule_type, status }) => rule_type === 'optionmaxamount' && !status
      )
      .map(({ checked_option }) => checked_option?.identifier) || [];
  const incompatibleOptionIdentifiers =
    state.configuration.information?.incompatibleOptions || [];
  const availableSlotsForType =
    state.configuration.information?.availableSlotsForType || {};
  const cpuAmount =
    state.configuration.optionclassifications
      ?.find(({ identifier }) => identifier === 'cpu')
      ?.selectedoptions?.reduce((total, { amount }) => total + amount, 0) || 0;
  const itemAttributes = state.configuration.item.attributes
    .filter(({ identifier }) => /(_[\d]+cpu)|(_cpus)$/.test(identifier))
    .map((attribute) => ({
      ...attribute,
      translated_title: attribute.translated_title || attribute.title
    }));
  const disabledComponentIdentifiers =
    state.configuration.information.blockedRaidLevels;
  state.groupedComponents = state.componentsWithOptions
    .reduce((groupedComponents: IComponentGroup[], component: IComponent) => {
      const configurationComponent =
        state.configuration?.optionclassifications.find(
          ({ identifier }) => identifier === component.identifier
        );
      setAmountStepsForCPUComponent(component);
      component.disabled = disabledComponentIdentifiers.includes(
        component.identifier
      );
      component.selectedoptions = configurationComponent?.selectedoptions;
      component.thumbnail = configurationComponent?.thumbnail;
      component.options = component.options.map((option) => {
        const configurationOption =
          configurationComponent?.selectedoptions?.find(
            ({ identifier }) => identifier === option.identifier
          );
        const amount = configurationOption?.amount || 0;
        const inputText = configurationOption?.inputText || '';
        return {
          ...option,
          maxAmount:
            (maxAmountChecks.includes(option.identifier) && amount) ||
            undefined,
          minAmount: 0,
          incompatible: incompatibleOptionIdentifiers.includes(
            option.identifier
          ),
          slotInfoExceeded: undefined,
          inputText,
          ...(state.configuration
            ? createSlotInfoForOption(
                option,
                availableSlotsForType,
                configurationComponent,
                cpuAmount,
                itemAttributes
              )
            : {}),
          ...(amount && state.configuration
            ? getSelectedDriveOptionsForRaidSlotConsumerOption(
                option,
                state.configuration
              )
            : {})
        };
      });
      if (component.identifier === WARRANTY_ADDONS_COMPONENT) {
        const warrantySelectedOption =
          state.configuration?.optionclassifications.find(
            ({ identifier }) => identifier === WARRANTY_COMPONENT
          )?.selectedoptions?.[0];

        component.options = filterWarrantyAddonOptions(
          state.warrantyAddonComponentOptions,
          warrantySelectedOption
        );
      }
      const groupAV = component.attributes.find(
        ({ identifier }) => identifier === 'step_server'
      )?.values?.[0];
      if (groupAV?.value) {
        let group = groupedComponents.find(
          ({ group: { value } }) => value === groupAV.value
        );
        if (!group) {
          group = { group: groupAV, components: [] };
          groupedComponents.push(group);
        }
        group.components.push(component);
      }
      return groupedComponents;
    }, [])
    .sort((a, b) => parseInt(a.group.value) - parseInt(b.group.value));
};

const checkExceededSlotTypes = (
  state: ConfigurationState,
  switchOptionData: { componentIdentifier: string; optionIdentifier: string }
) => {
  const exceededSlots = Object.values(
    state.configuration?.information?.exceededSlotTypes || {}
  );
  if (!exceededSlots.length) {
    return;
  }
  state.groupedComponents?.forEach(({ components }) => {
    const option = components
      .find(
        ({ identifier }) => identifier === switchOptionData.componentIdentifier
      )
      ?.options.find(
        ({ identifier }) => identifier === switchOptionData.optionIdentifier
      );
    if (!option) {
      return;
    }
    option.slotInfoExceeded = exceededSlots;
  });
};

const updateErrorMessage = (state: ConfigurationState) => {
  const removedOptions = state.configuration?.information?.removedOptions;
  if (removedOptions?.length) {
    state.errorMessage = {
      translationKey: 'removedOptionsMessage',
      listItems: state.componentsWithOptions
        .map(({ options, title: componentTitle }) =>
          options
            .filter(({ identifier }) => removedOptions.includes(identifier))
            .map(
              ({ title: optionTitle }) => `${optionTitle} (${componentTitle})`
            )
        )
        .flat()
    };
  } else {
    state.errorMessage = undefined;
  }
};

export const authSlice = createSlice({
  name: 'configuration',
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    loadConfigurationSuccess: (state, action) => {
      state.configuration = action.payload;
      updateErrorMessage(state);
    },
    loadComponentsWithOptionsSuccess: (state, action) => {
      state.componentsWithOptions = action.payload;
      state.componentsWithOptions.forEach(setOptionFiltersForComponent);
      state.warrantyAddonComponentOptions =
        state.componentsWithOptions.find(
          ({ identifier }) => identifier === WARRANTY_ADDONS_COMPONENT
        )?.options || [];
      updateGroupedComponents(state);
    },
    switchOptionsSuccess: (state, action) => {
      if (state.configuration) {
        state.configuration = action.payload.responseData;
        updateGroupedComponents(state);
        checkExceededSlotTypes(state, action.payload.switchOptionData);
        updateErrorMessage(state);
      }
    },
    calculationSuccess: (state, action) => {
      state.calculation = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage =
        typeof action.payload === 'string'
          ? { translationKey: action.payload }
          : action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setShareUrl: (state, action) => {
      state.shareUrl = action.payload.shareUrl;
      state.shareCode = action.payload.code;
    },
    setOptionInputText: (
      state,
      action: PayloadAction<{
        componentIdentifier: string;
        optionIdentifier: string;
        inputText: string;
      }>
    ) => {
      const { componentIdentifier, optionIdentifier, inputText } =
        action.payload;
      const selectedOption = state.configuration?.optionclassifications
        .find(({ identifier }) => componentIdentifier === identifier)
        ?.selectedoptions?.find(
          ({ identifier }) => optionIdentifier === identifier
        );
      if (selectedOption) {
        selectedOption.inputText = inputText;
      }
    },
    setInvalidState: (state) => {
      state.invalidState = true;
    }
  }
});

export const {
  loadConfigurationSuccess,
  loadComponentsWithOptionsSuccess,
  switchOptionsSuccess,
  calculationSuccess,
  setErrorMessage,
  setIsLoading,
  setShareUrl,
  reset,
  setOptionInputText,
  setInvalidState
} = authSlice.actions;

export default authSlice.reducer;

const hasInvalidSlotAmount = (configuration: IConfiguration) =>
  Object.values(configuration?.information?.availableSlotsForType || {}).some(
    (v) => v < 0
  );

export const loadInitialConfiguration = () => (dispatch: AppDispatch) => {
  const params = {
    identifier: false,
    code: false
  };
  const basePath = '/';
  const itemRegex = new RegExp(`${basePath}(code|identifier):([^/]+)`);
  const results = itemRegex.exec(location.pathname);
  if (results) {
    dispatch(setIsLoading(true));
    // @ts-ignore
    params[results[1]] = results[2];
    let urlPart = '';
    if (params.identifier) {
      urlPart = `loadbyitemidentifier/${params.identifier}`;
    } else if (params.code) {
      urlPart = `loadbyconfigurationcode/${params.code}`;
    }
    Api.request(`/configuration/${urlPart}`)
      .then((response) => {
        dispatch(reset());
        const action = dispatch(loadConfigurationSuccess(response.data));
        if (hasInvalidSlotAmount(response.data)) {
          dispatch(setInvalidState());
          dispatch(setErrorMessage('invalidSlotAmountMessage'));
        }
        return action;
      })
      .then((action) => {
        const itemIdentifier = action.payload.item.identifier;
        return Api.request(
          `/items/${itemIdentifier}/optionclassificationswithoptions`
        );
      })
      .then((response) => {
        return dispatch(loadComponentsWithOptionsSuccess(response.data));
      })
      .catch((e) => {
        console.error(e);
        return dispatch(setErrorMessage(e.message));
      })
      .finally(() => dispatch(setIsLoading(false)));
  } else {
    dispatch(setErrorMessage('no route provided'));
  }
};

export const loadbyconfigurationcode =
  (code: string) => async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    let urlPart = '';
    {
      urlPart = `loadbyconfigurationcode/${code}`;
    }
    return Api.request(`/configuration/${urlPart}`)
      .then((response) => {
        return dispatch(loadConfigurationSuccess(response.data));
      })
      .catch((e) => {
        return dispatch(e);
      })
      .finally(() => dispatch(setIsLoading(false)));
  };

export const switchOptions =
  (
    componentIdentifier: string,
    optionIdentifier: string,
    amount = 1,
    userNote = ''
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setIsLoading(true));
    const {
      configuration,
      componentsWithOptions,
      warrantyAddonComponentOptions
    } = getState().configuration;
    const { selectedoptions: selectedOptions, is_multiselect: isMultiselect } =
      configuration?.optionclassifications.find(
        ({ identifier }) => identifier === componentIdentifier
      ) || {};
    const option =
      selectedOptions?.find(
        ({ identifier }) => identifier === optionIdentifier
      ) ||
      componentsWithOptions
        ?.find(({ identifier }) => identifier === componentIdentifier)
        ?.options?.find(({ identifier }) => identifier === optionIdentifier);
    const isAlreadySelected = selectedOptions?.some(
      ({ identifier }) => identifier === optionIdentifier
    );
    const switchedOptions = {
      [componentIdentifier]: [
        {
          ...option,
          amount: option?.amount_is_selectable
            ? amount
            : isMultiselect && isAlreadySelected
            ? 0
            : amount,
          userNote
        }
      ]
    };
    if (componentIdentifier === WARRANTY_COMPONENT) {
      const selectedWarrantyAddon = configuration?.optionclassifications.find(
        ({ identifier }) => identifier === WARRANTY_ADDONS_COMPONENT
      )?.selectedoptions?.[0];
      if (selectedWarrantyAddon) {
        const warrantyAddonsToSwitch = getWarrantyAddonOptionsForWarrantyOption(
          optionIdentifier,
          selectedWarrantyAddon,
          componentsWithOptions,
          warrantyAddonComponentOptions
        );
        if (warrantyAddonsToSwitch) {
          switchedOptions[WARRANTY_ADDONS_COMPONENT] = warrantyAddonsToSwitch;
        }
      }
    }
    const data = { configuration, switchedOptions };
    return Api.request({
      url: `configuration/switchoption`,
      method: 'post',
      data
    })
      .then((response) => {
        return dispatch(
          switchOptionsSuccess({
            responseData: response.data,
            switchOptionData: { componentIdentifier, optionIdentifier }
          })
        );
      })
      .catch((e) => {
        if (e.response && e.response.status === 400) {
          return dispatch(setErrorMessage(e.response.data.message));
        }
        return;
      })
      .finally(() => dispatch(setIsLoading(false)));
  };

interface IErrorItem {
  componentTitle?: string;
  errors: {
    message: string;
  }[];
}

export const validateConfiguration =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const configuration = getState().configuration.configuration;
    Api.request({
      url: `configuration/validate`,
      method: 'post',
      data: { configuration }
    }).catch((result) => {
      const errors = Object.values(result.response.data.errors)
        .map((item) =>
          (item as IErrorItem).errors.map((error) => ({
            translationKey: `validationErrors.${error.message}`,
            listItem: (item as IErrorItem).componentTitle
          }))
        )
        .flat()
        .reduce((res: ErrorMessage[], item) => {
          let err = res.find(
            ({ translationKey }) => translationKey === item.translationKey
          );
          if (!err) {
            err = { translationKey: item.translationKey, listItems: [] };
            res.push(err);
          }
          item.listItem && err.listItems.push(item.listItem);
          return res;
        }, []);
      dispatch(setErrorMessage(errors));
    });
  };

export const saveConfigurationForCart =
  (code?: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setIsLoading(true));
    const configuration = {
      ...getState().configuration.configuration,
      code
    };
    const data = {
      configurationtype: 'cart',
      configuration
    };

    Api.request({ url: `configurations`, method: 'post', data })
      .then((response) => {
        if (response.data && response.data.code) {
          if (
            window.parent !== undefined &&
            window.parent.postMessage !== undefined
          ) {
            window.parent.postMessage(
              { method: METHOD_ADD_TO_CART, code: response.data.code },
              '*'
            );
          }
        }
      })
      .catch((e) => {
        return dispatch(e);
      })
      .finally(() => dispatch(setIsLoading(false)));
  };

export const saveConfigurationForSharing =
  (itemIdentifier: string, selectedOptions: ISelectedOptions) =>
  (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    const data = {
      code: undefined,
      itemIdentifier,
      configurationType: 'share',
      selectedOptions
    };

    Api.request({ url: `configurations`, method: 'post', data })
      .then((response) => {
        if (response.data && response.data.shareUrl) {
          dispatch(setShareUrl(response.data));
        }
      })
      .catch((e) => {
        return dispatch(e);
      })
      .finally(() => dispatch(setIsLoading(false)));
  };

startAppListening({
  predicate: (_, currentState: RootState, previousState: RootState) => {
    // return true when the listener should run
    return (
      currentState.configuration.configuration !==
      previousState.configuration.configuration
    );
  },
  effect: (action, listenerApi) => {
    const configuration = listenerApi.getState().configuration.configuration;
    if (!configuration) {
      return;
    }
    Api.request({
      url: `calculation`,
      method: 'post',
      data: listenerApi.getState().configuration.configuration
    })
      .then((response) => {
        return listenerApi.dispatch(calculationSuccess(response.data));
      })
      .catch((e) => {
        console.error(e);
        return listenerApi.dispatch(setErrorMessage(e.message));
      });
  }
});
